
import { AnalyticsEvents } from './events';
import { AnalyticsProvider, getProvider } from './providers';

/**
 * Core Analytics Service
 */
class AnalyticsService {
  private provider: AnalyticsProvider;
  private initialized = false;

  constructor() {
    this.provider = getProvider();
  }

  /**
   * Initialize the analytics service
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;
    
    try {
      await this.provider.initialize();
      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize analytics:', error);
    }
  }

  /**
   * Track a custom event
   */
  trackEvent(name: string, params?: Record<string, any>): void {
    if (!this.initialized) return;
    this.provider.trackEvent(name, params);
  }

  /**
   * Track a page view
   */
  trackPageView(path: string, title?: string): void {
    if (!this.initialized) return;
    this.provider.trackPageView(path, title);
  }

  /**
   * Set user properties
   */
  setUserProperties(properties: Record<string, any>): void {
    if (!this.initialized) return;
    this.provider.setUserProperties(properties);
  }

  /**
   * Identify a user
   */
  identify(userId: string, properties?: Record<string, any>): void {
    if (!this.initialized) return;
    this.provider.identify(userId, properties);
  }

  /**
   * Reset the current user (for logout)
   */
  reset(): void {
    if (!this.initialized) return;
    this.provider.reset();
  }

  // Convenience methods for common events
  
  /**
   * Track a login event
   */
  trackLogin(method: string, success: boolean): void {
    this.trackEvent(AnalyticsEvents.LOGIN, {
      auth_method: method,
      success,
    });
  }

  /**
   * Track a signup event
   */
  trackSignup(method: string, success: boolean): void {
    this.trackEvent(AnalyticsEvents.SIGNUP, {
      auth_method: method,
      success,
    });
  }

  /**
   * Track a connection created event
   */
  trackConnectionCreated(connectionId: string, connectionName: string): void {
    this.trackEvent(AnalyticsEvents.CONNECTION_CREATED, {
      connection_id: connectionId,
      connection_name: connectionName,
    });
  }

  /**
   * Track a connection paired event
   */
  trackConnectionPaired(connectionId: string): void {
    this.trackEvent(AnalyticsEvents.CONNECTION_PAIRED, {
      connection_id: connectionId,
    });
  }

  /**
   * Track a button click
   */
  trackButtonClick(buttonName: string, params?: Record<string, any>): void {
    this.trackEvent(AnalyticsEvents.BUTTON_CLICK, {
      button_name: buttonName,
      ...params,
    });
  }
}

// Create a singleton instance
export const analytics = new AnalyticsService();
